import { useEffect, useCallback } from 'react';
import { getSiteConfig } from '@/utils/siteConfigStore';

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
  type: 'consultation' | 'franchise';
}

export default function ContactModal({ open, onClose, type }: ContactModalProps) {
  const siteConfig = getSiteConfig();

  const modalData = type === 'consultation' ? siteConfig.consultationModal : siteConfig.franchiseModal;

  const content = type === 'consultation'
    ? {
        icon: 'ri-user-heart-line',
        title: '预约专属顾问',
        subtitle: '添加微信，获取一对一健康纤体方案咨询',
        qrLabel: '扫码添加顾问微信',
        features: ['免费体测评估', '个性化方案定制', '门店体验预约', '健康知识分享'],
        footerText: '扫描二维码或拨打热线，我们将在第一时间与您取得联系',
      }
    : {
        icon: 'ri-briefcase-line',
        title: '申请加盟合作',
        subtitle: '添加微信，了解招商政策与区域合作详情',
        qrLabel: '扫码添加招商负责人微信',
        features: ['招商政策解读', '区域保护机制', '投资回报分析', '门店考察安排'],
        footerText: '扫描二维码或拨打招商热线，招商经理将尽快与您联系',
      };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl overflow-hidden shadow-2xl transform transition-all duration-300 scale-100">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="p-8 md:p-10">
          <div className="text-center mb-8">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-brand-light">
              <i className={`${content.icon} text-brand-deep text-xl`} />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-brand-deep mb-2">
              {content.title}
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed">
              {content.subtitle}
            </p>
          </div>

          <div className="flex flex-col items-center mb-8">
            <div className="w-48 h-48 bg-gray-50 rounded-xl overflow-hidden border border-gray-100 mb-3">
              <img
                src={modalData.qrImage}
                alt={content.qrLabel}
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <i className="ri-qr-scan-line" />
              {content.qrLabel}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {content.features.map((feature) => (
              <div key={feature} className="flex items-center gap-2 px-3 py-2.5 bg-brand-light/50 rounded-lg">
                <i className="ri-check-line text-brand-deep/70 text-sm" />
                <span className="text-xs text-gray-600">{feature}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-light/60 flex-shrink-0">
                  <i className="ri-phone-line text-brand-deep text-sm" />
                </div>
                <div>
                  <span className="text-xs text-gray-400">{modalData.phoneLabel}</span>
                  <span className="block tracking-wide text-gray-600 font-medium">{modalData.phone}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-light/60 flex-shrink-0">
                  <i className="ri-time-line text-brand-deep text-sm" />
                </div>
                <span>{modalData.hours}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-light/60 flex-shrink-0">
                  <i className="ri-map-pin-line text-brand-deep text-sm" />
                </div>
                <span>{modalData.address}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="px-8 pb-6">
          <p className="text-center text-xs text-gray-400">
            {content.footerText}
          </p>
        </div>
      </div>
    </div>
  );
}